import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  addDays,
  differenceInCalendarDays,
  eachWeekOfInterval,
  endOfDay,
  format,
  max as maxDate,
  min as minDate,
  parseISO,
  startOfDay,
} from 'date-fns';
import { de } from 'date-fns/locale';
import type { Task } from '@shared/types';
import { cn, dueStatus, DUE_CLASSES } from '@/lib/format';
import { EmptyState } from './ui';
import { CalendarClock } from 'lucide-react';

/**
 * A lightweight timeline. Each task with a date gets a bar from its start (or
 * due date, if no start) to its due/end date. No dependency arrows — the legacy
 * Gantt didn't use them and the value here is "what's happening when", which a
 * simple bar chart conveys without a heavy library.
 *
 * Undated tasks can't be placed on a timeline, so they're listed separately
 * rather than silently dropped.
 */
const DAY_PX = 28; // column width per day
const ROW_H = 30;

type StatusFilter = 'open' | 'done' | 'all';

export function GanttView({ tasks }: { tasks: Task[] }) {
  const [scrollDays] = useState(0);
  // Default to OPEN — a timeline of what's still to do is the common need, and
  // it's why an all-done board looked like a wall of grey bars.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('open');

  // Apply the done/open filter before splitting dated vs undated.
  const filtered = useMemo(
    () =>
      tasks.filter((t) =>
        statusFilter === 'all' ? true : statusFilter === 'done' ? t.done : !t.done,
      ),
    [tasks, statusFilter],
  );
  const dated = useMemo(() => filtered.filter((t) => t.dueDate || t.startDate), [filtered]);
  const undated = useMemo(() => filtered.filter((t) => !t.dueDate && !t.startDate), [filtered]);

  // Counts for the filter labels, computed off the full set so they don't
  // change as the filter changes.
  const counts = useMemo(
    () => ({
      open: tasks.filter((t) => !t.done && (t.dueDate || t.startDate)).length,
      done: tasks.filter((t) => t.done && (t.dueDate || t.startDate)).length,
    }),
    [tasks],
  );

  const range = useMemo(() => {
    if (dated.length === 0) return null;
    const points: Date[] = [];
    for (const t of dated) {
      if (t.startDate) points.push(parseISO(t.startDate));
      if (t.dueDate) points.push(parseISO(t.dueDate));
      if (t.endDate) points.push(parseISO(t.endDate));
    }
    // Pad a week either side so bars don't butt against the edges.
    const start = startOfDay(addDays(minDate(points), -3));
    const end = endOfDay(addDays(maxDate(points), 7));
    return { start, end, totalDays: differenceInCalendarDays(end, start) + 1 };
  }, [dated]);

  // Done/open/all segmented control, shown above the timeline in both the
  // populated and empty states so a filter is never a dead end.
  const filterBar = (
    <div className="flex items-center gap-3 border-b line surface-card px-4 py-2 text-xs">
      <div className="flex rounded-lg surface-sunken p-0.5">
        {(
          [
            ['open', `Offen${counts.open ? ` (${counts.open})` : ''}`],
            ['done', `Erledigt${counts.done ? ` (${counts.done})` : ''}`],
            ['all', 'Alle'],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            onClick={() => setStatusFilter(value)}
            className={cn(
              'rounded-md px-2.5 py-1 font-medium transition-colors',
              statusFilter === value ? 'surface-card text-strong shadow-sm' : 'text-muted hover:text-strong',
            )}
          >
            {label}
          </button>
        ))}
      </div>
      <span className="text-faint">
        {dated.length} auf der Zeitleiste
        {undated.length > 0 && ` · ${undated.length} ohne Datum`}
      </span>
    </div>
  );

  if (dated.length === 0) {
    return (
      <div className="flex h-full flex-col">
        {filterBar}
        <EmptyState
          icon={<CalendarClock />}
          title={statusFilter === 'done' ? 'Keine erledigten terminierten Aufgaben' : 'Nichts terminiert'}
          description="Aufgaben mit Start- oder Fälligkeitsdatum erscheinen hier als Zeitleiste."
        />
      </div>
    );
  }

  // Bar colour by due-status so the timeline conveys urgency at a glance:
  // done = grey, overdue = red, due soon = amber, otherwise brand green.
  function barColour(t: Task, status: ReturnType<typeof dueStatus>): string {
    if (t.done) return 'bg-[var(--line-strong)]';
    if (status === 'overdue') return 'bg-red-500';
    if (status === 'today' || status === 'tomorrow') return 'bg-orange-500';
    if (status === 'soon') return 'bg-amber-500';
    return 'bg-brand-500';
  }

  const { start, totalDays } = range as NonNullable<typeof range>;
  const weeks = eachWeekOfInterval(
    { start, end: addDays(start, totalDays) },
    { weekStartsOn: 1 },
  );
  const today = startOfDay(new Date());
  const todayOffset = differenceInCalendarDays(today, start);

  // Default span for a task that has only a due date (the overwhelming case in
  // this data): draw a bar of this many days ENDING on the due date, so it
  // reads as "work leading up to the deadline" rather than a 1px dot.
  const DEFAULT_SPAN_DAYS = 5;

  function barFor(t: Task): { left: number; width: number; milestone: boolean } {
    const hasStart = Boolean(t.startDate);
    const due = t.dueDate ? parseISO(t.dueDate) : null;
    const startPt = t.startDate ? parseISO(t.startDate) : null;
    const endPt = t.endDate ? parseISO(t.endDate) : due;

    let s: Date;
    let e: Date;
    if (hasStart && endPt) {
      // Real range: start → end/due.
      s = startOfDay(startPt as Date);
      e = startOfDay(endPt);
    } else if (due) {
      // Due-date-only: a DEFAULT_SPAN_DAYS bar ending on the due date.
      e = startOfDay(due);
      s = addDays(e, -(DEFAULT_SPAN_DAYS - 1));
    } else {
      // Start-only: a default bar starting there.
      s = startOfDay(startPt as Date);
      e = addDays(s, DEFAULT_SPAN_DAYS - 1);
    }

    const left = differenceInCalendarDays(s, start) * DAY_PX;
    const width = (differenceInCalendarDays(e, s) + 1) * DAY_PX;
    // A genuine single-day task (real start==end) is shown as a milestone dot.
    const milestone = hasStart && Boolean(endPt) && differenceInCalendarDays(e, s) === 0;
    return { left, width, milestone };
  }

  return (
    <div className="flex h-full flex-col">
      {filterBar}
      <div className="scroll-thin flex-1 overflow-auto">
        <div className="relative" style={{ width: totalDays * DAY_PX + 240 }}>
          {/* Week header */}
          <div className="sticky top-0 z-10 flex border-b line surface-raised" style={{ marginLeft: 240 }}>
            {weeks.map((w) => (
              <div
                key={w.toISOString()}
                className="shrink-0 border-r line px-2 py-1.5 text-[11px] text-muted"
                style={{ width: DAY_PX * 7 }}
              >
                KW {format(w, 'w', { locale: de })} · {format(w, 'd. MMM', { locale: de })}
              </div>
            ))}
          </div>

          {/* Today marker */}
          {todayOffset >= 0 && todayOffset <= totalDays && (
            <div
              className="pointer-events-none absolute bottom-0 top-8 z-0 w-px bg-brand-400"
              style={{ left: 240 + todayOffset * DAY_PX + scrollDays }}
              aria-hidden
            />
          )}

          {/* Rows */}
          <div>
            {dated.map((t) => {
              const bar = barFor(t);
              const status = dueStatus(t.dueDate, t.done);
              return (
                <div key={t.id} className="flex items-center border-b border-[var(--line-light,transparent)]" style={{ height: ROW_H }}>
                  <div className="sticky left-0 z-10 flex h-full w-60 shrink-0 items-center gap-2 border-r line surface-card px-3">
                    <Link
                      to="/tasks/$taskId"
                      params={{ taskId: t.id }}
                      className={cn('truncate text-[12px] hover:text-brand-500 hover:underline', t.done ? 'text-muted line-through' : 'text-body')}
                      title={t.title}
                    >
                      {t.title}
                    </Link>
                  </div>
                  <div className="relative h-full flex-1">
                    {bar.milestone ? (
                      // Genuine single-day item: a diamond marker, not a bar.
                      <div
                        className={cn('absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-[2px]', barColour(t, status))}
                        style={{ left: bar.left }}
                        title={t.dueDate ? format(parseISO(t.dueDate), 'd. MMM yyyy', { locale: de }) : ''}
                      />
                    ) : (
                      <div
                        className={cn('absolute top-1/2 h-5 -translate-y-1/2 rounded-md shadow-sm', barColour(t, status))}
                        style={{ left: bar.left, width: bar.width, minWidth: 20 }}
                        title={`${t.startDate ? format(parseISO(t.startDate), 'd. MMM', { locale: de }) + ' – ' : 'fällig '}${t.dueDate ? format(parseISO(t.dueDate), 'd. MMM yyyy', { locale: de }) : ''}`}
                      />
                    )}
                    {/* Due date label sits just past the bar's end. */}
                    <span
                      className={cn('absolute top-1/2 -translate-y-1/2 whitespace-nowrap pl-1.5 text-[10px]', DUE_CLASSES[status])}
                      style={{ left: bar.left + bar.width }}
                    >
                      {t.dueDate ? format(parseISO(t.dueDate), 'd. MMM', { locale: de }) : ''}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
